import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { vehiclesAPI, clientsAPI, devicesAPI, simsAPI, providersAPI } from '../services/api';

function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [deviceDetailDialog, setDeviceDetailDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [editDeviceDialog, setEditDeviceDialog] = useState(false);
  const [assignSimDialog, setAssignSimDialog] = useState(false);
  const [availableSims, setAvailableSims] = useState([]);
  const [selectedSim, setSelectedSim] = useState('');
  const [deviceFormData, setDeviceFormData] = useState({ model: '', notes: '' });
  const [editSimDialog, setEditSimDialog] = useState(false);
  const [editingSimData, setEditingSimData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    registration_number: '',
    client: '',
    current_device: '',
    installation_date: '',
    brand: '',
    model: '',
    year: '',
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const vehiclesRes = await vehiclesAPI.getAll();
      console.log('Loaded vehicles:', vehiclesRes.data);
      // Log first vehicle to check fields
      if (vehiclesRes.data.length > 0) {
        console.log('First vehicle sample:', vehiclesRes.data[0]);
      }
      setVehicles(vehiclesRes.data);
      setError(null);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Failed to load vehicles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDialogData = async () => {
    // Only load clients and devices when dialog opens
    if (clients.length === 0 || devices.length === 0) {
      try {
        setLoadingDialog(true);
        const [clientsRes, devicesRes] = await Promise.all([
          clients.length === 0 ? clientsAPI.getAll() : Promise.resolve({ data: clients }),
          devices.length === 0 ? devicesAPI.getAllBulk() : Promise.resolve({ data: devices }),
        ]);
        setClients(clientsRes.data);
        setDevices(devicesRes.data);
      } catch (err) {
        console.error('Error loading dialog data:', err);
      } finally {
        setLoadingDialog(false);
      }
    }
  };

  const handleOpenDialog = async (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        name: vehicle.name,
        registration_number: vehicle.registration_number || '',
        client: vehicle.client || '',
        current_device: vehicle.current_device || '',
        installation_date: vehicle.installation_date || '',
        brand: vehicle.brand || '',
        model: vehicle.model || '',
        year: vehicle.year || '',
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        name: '',
        registration_number: '',
        client: '',
        current_device: '',
        installation_date: '',
        brand: '',
        model: '',
        year: '',
      });
    }
    setOpenDialog(true);
    await loadDialogData();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVehicle(null);
    setFormData({
      name: '',
      registration_number: '',
      client: '',
      current_device: '',
      installation_date: '',
      brand: '',
      model: '',
      year: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const dataToSubmit = {
        ...formData,
        registration_number: formData.registration_number || null,
        client: formData.client || null,
        current_device: formData.current_device || null,
        installation_date: formData.installation_date || null,
        brand: formData.brand || null,
        model: formData.model || null,
        year: formData.year || null,
      };

      if (editingVehicle) {
        await vehiclesAPI.update(editingVehicle.id, dataToSubmit);
        alert('Véhicule mis à jour avec succès!');
      } else {
        await vehiclesAPI.create(dataToSubmit);
        alert('Véhicule créé avec succès!');
      }
      handleCloseDialog();
      loadVehicles();
    } catch (err) {
      console.error('Error saving vehicle:', err);
      alert('Erreur lors de la sauvegarde: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule?')) {
      try {
        await vehiclesAPI.delete(id);
        alert('Véhicule supprimé avec succès!');
        loadVehicles();
      } catch (err) {
        console.error('Error deleting vehicle:', err);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  const filteredVehicles = vehicles.filter((vehicle) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      vehicle.name.toLowerCase().includes(searchLower) ||
      (vehicle.registration_number && vehicle.registration_number.toLowerCase().includes(searchLower)) ||
      (vehicle.client_name && vehicle.client_name.toLowerCase().includes(searchLower)) ||
      (vehicle.device_imei && vehicle.device_imei.toLowerCase().includes(searchLower)) ||
      (vehicle.brand && vehicle.brand.toLowerCase().includes(searchLower)) ||
      (vehicle.model && vehicle.model.toLowerCase().includes(searchLower)) ||
      (vehicle.installation_date && vehicle.installation_date.includes(searchLower)) ||
      (vehicle.server_name && vehicle.server_name.toLowerCase().includes(searchLower))
    );
  }).sort(getComparator(order, orderBy));

  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeviceClick = async (deviceId) => {
    if (!deviceId) return;
    try {
      const response = await devicesAPI.get(deviceId);
      setSelectedDevice(response.data);
      setDeviceDetailDialog(true);
    } catch (err) {
      console.error('Error loading device details:', err);
      alert('Erreur lors du chargement des détails du dispositif');
    }
  };

  const handleCloseDeviceDetail = () => {
    setDeviceDetailDialog(false);
    setSelectedDevice(null);
  };

  const handleEditDevice = () => {
    if (selectedDevice) {
      setDeviceFormData({
        model: selectedDevice.model || '',
        notes: selectedDevice.notes || ''
      });
      setEditDeviceDialog(true);
    }
  };

  const handleSaveDevice = async () => {
    if (!selectedDevice) return;
    try {
      await devicesAPI.update(selectedDevice.id, deviceFormData);
      alert('Dispositif mis à jour avec succès!');
      setEditDeviceDialog(false);
      // Reload device details
      const response = await devicesAPI.get(selectedDevice.id);
      setSelectedDevice(response.data);
      // Reload vehicles to update the list
      loadVehicles();
    } catch (err) {
      console.error('Error updating device:', err);
      alert('Erreur lors de la mise à jour du dispositif');
    }
  };

  const handleOpenAssignSim = async () => {
    try {
      // Load available SIMs (status = 'available')
      const response = await simsAPI.getAll({ status: 'available' });
      setAvailableSims(response.data.results || response.data);
      setSelectedSim('');
      setAssignSimDialog(true);
    } catch (err) {
      console.error('Error loading available SIMs:', err);
      alert('Erreur lors du chargement des cartes SIM disponibles');
    }
  };

  const handleAssignSim = async () => {
    if (!selectedDevice || !selectedSim) return;
    
    try {
      // Get the current SIM data
      const simResponse = await simsAPI.get(selectedSim);
      const simData = simResponse.data;
      
      // Assign existing SIM to device
      await simsAPI.update(selectedSim, {
        iccid: simData.iccid,
        sim_number: simData.sim_number,
        provider: simData.provider,
        device: selectedDevice.id,
        notes: simData.notes || ''
      });
      
      alert('Carte SIM assignée avec succès!');
      setAssignSimDialog(false);
      
      // Reload device details
      const response = await devicesAPI.get(selectedDevice.id);
      setSelectedDevice(response.data);
      
      // Reload vehicles to update the list
      loadVehicles();
    } catch (err) {
      console.error('Error assigning SIM:', err);
      const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      alert(`Erreur lors de l'assignation: ${errorMsg}`);
    }
  };

  const handleEditSim = (sim) => {
    setEditingSimData({
      id: sim.id,
      sim_number: sim.sim_number,
      iccid: sim.iccid,
      notes: ''
    });
    setEditSimDialog(true);
  };

  const handleSaveSim = async () => {
    if (!editingSimData) return;
    
    try {
      const simResponse = await simsAPI.get(editingSimData.id);
      const simData = simResponse.data;
      
      await simsAPI.update(editingSimData.id, {
        iccid: editingSimData.iccid,
        sim_number: editingSimData.sim_number,
        provider: simData.provider,
        device: simData.device,
        notes: editingSimData.notes || simData.notes || ''
      });
      
      alert('Carte SIM mise à jour avec succès!');
      setEditSimDialog(false);
      
      // Reload device details
      const response = await devicesAPI.get(selectedDevice.id);
      setSelectedDevice(response.data);
      
      // Reload vehicles
      loadVehicles();
    } catch (err) {
      console.error('Error updating SIM:', err);
      alert('Erreur lors de la mise à jour de la carte SIM');
    }
  };

  const handleDeleteSim = async (simId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir dissocier cette carte SIM du dispositif?')) {
      return;
    }
    
    try {
      const simResponse = await simsAPI.get(simId);
      const simData = simResponse.data;
      
      // Unassign SIM from device
      await simsAPI.update(simId, {
        iccid: simData.iccid,
        sim_number: simData.sim_number,
        provider: simData.provider,
        device: null,
        notes: simData.notes || ''
      });
      
      alert('Carte SIM dissociée avec succès!');
      
      // Reload device details
      const response = await devicesAPI.get(selectedDevice.id);
      setSelectedDevice(response.data);
      
      // Reload vehicles
      loadVehicles();
    } catch (err) {
      console.error('Error deleting SIM:', err);
      alert('Erreur lors de la dissociation de la carte SIM');
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Véhicules
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadVehicles}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Véhicule
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, immatriculation, client, marque ou modèle..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  <strong>Nom</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'registration_number'}
                  direction={orderBy === 'registration_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('registration_number')}
                >
                  <strong>Immatriculation</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('client_name')}
                >
                  <strong>Client</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'device_imei'}
                  direction={orderBy === 'device_imei' ? order : 'asc'}
                  onClick={() => handleRequestSort('device_imei')}
                >
                  <strong>Dispositif GPS</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'installation_date'}
                  direction={orderBy === 'installation_date' ? order : 'asc'}
                  onClick={() => handleRequestSort('installation_date')}
                >
                  <strong>Date d'installation</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'server_name'}
                  direction={orderBy === 'server_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('server_name')}
                >
                  <strong>Serveur</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVehicles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucun véhicule trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedVehicles.map((vehicle) => (
                <TableRow key={vehicle.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CarIcon color="primary" />
                      <strong>{vehicle.name}</strong>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {vehicle.registration_number ? (
                      <Chip label={vehicle.registration_number} size="small" color="primary" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{vehicle.client_name || <Typography variant="body2" color="text.secondary">Non assigné</Typography>}</TableCell>
                  <TableCell>
                    {vehicle.device_imei ? (
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="caption" display="block"><strong>Modèle:</strong> {vehicle.device_model || 'N/A'}</Typography>
                            <Typography variant="caption" display="block"><strong>SIM:</strong> {vehicle.device_sim_number || 'N/A'}</Typography>
                          </Box>
                        }
                        arrow
                      >
                        <Chip
                          label={vehicle.device_imei}
                          color="success"
                          size="small"
                          onClick={() => handleDeviceClick(vehicle.current_device)}
                          onDelete={() => handleDeviceClick(vehicle.current_device)}
                          deleteIcon={<InfoIcon />}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="Non assigné" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {vehicle.installation_date ? (
                      <Typography variant="body2">
                        {new Date(vehicle.installation_date).toLocaleDateString('fr-FR')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {vehicle.server_name ? (
                      <Chip
                        label={vehicle.server_name}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(vehicle)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(vehicle.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredVehicles.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVehicle ? 'Modifier le véhicule' : 'Nouveau véhicule'}
        </DialogTitle>
        <DialogContent>
          {loadingDialog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Nom *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                helperText="Nom du véhicule (requis)"
              />

              <TextField
                label="Immatriculation"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                fullWidth
                helperText="Numéro d'immatriculation (optionnel)"
              />

              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.client}
                  label="Client"
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Aucun</em>
                  </MenuItem>
                  {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.client_code} - {client.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Dispositif GPS *</InputLabel>
              <Select
                value={formData.current_device}
                label="Dispositif GPS *"
                onChange={(e) => setFormData({ ...formData, current_device: e.target.value })}
                MenuProps={{
                  PaperProps: {
                    style: { maxWidth: 600 }
                  }
                }}
              >
                {devices
                  .filter(d => d.status === 'stock' || d.id === formData.current_device)
                  .map((device) => (
                    <MenuItem key={device.id} value={device.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span style={{ fontWeight: 500 }}>{device.imei}</span>
                      <span style={{ color: '#666', fontSize: '0.9em' }}>({device.model})</span>
                      {device.server_name && (
                        <span style={{ 
                          backgroundColor: '#e3f2fd', 
                          color: '#1976d2', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '0.75em',
                          marginLeft: 'auto'
                        }}>
                          🔗 {device.server_name}
                        </span>
                      )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              label="Date d'installation"
              type="date"
              value={formData.installation_date}
              onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Marque"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              fullWidth
            />

            <TextField
              label="Modèle"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              fullWidth
            />

            <TextField
              label="Année"
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              fullWidth
            />
          </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.current_device}
          >
            {editingVehicle ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Device Detail Dialog */}
      <Dialog open={deviceDetailDialog} onClose={handleCloseDeviceDetail} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Détails du Dispositif</Typography>
          <IconButton onClick={handleCloseDeviceDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDevice ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">IMEI</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedDevice.imei}</Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary">Modèle</Typography>
                <Typography variant="body1">{selectedDevice.model || 'Non spécifié'}</Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary">Statut</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={selectedDevice.status === 'stock' ? 'En stock' : 
                           selectedDevice.status === 'installed' ? 'Installé' : 
                           selectedDevice.status === 'maintenance' ? 'Maintenance' : 
                           selectedDevice.status}
                    color={selectedDevice.status === 'installed' ? 'success' : 
                           selectedDevice.status === 'stock' ? 'default' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Cartes SIM ({selectedDevice.sim && Array.isArray(selectedDevice.sim) ? selectedDevice.sim.length : 0})
                </Typography>
                {selectedDevice.sim && Array.isArray(selectedDevice.sim) && selectedDevice.sim.length > 0 ? (
                  <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDevice.sim.map((sim, index) => (
                      <Paper key={sim.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            SIM #{index + 1}
                          </Typography>
                          <Box>
                            <IconButton size="small" onClick={() => handleEditSim(sim)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteSim(sim.id)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="body2">
                          <strong>Numéro:</strong> {sim.sim_number || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>ICCID:</strong> {sim.iccid || 'N/A'}
                        </Typography>
                        {sim.provider_name && (
                          <Typography variant="body2">
                            <strong>Opérateur:</strong> {sim.provider_name}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <strong>Statut:</strong> {sim.status === 'assigned' ? 'Assignée' : 
                                                     sim.status === 'available' ? 'Disponible' : 
                                                     sim.status}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Aucune carte SIM assignée
                  </Typography>
                )}
              </Box>
              
              {selectedDevice.synced_from_server && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Serveur</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={selectedDevice.synced_from_server.name}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}
              
              {selectedDevice.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedDevice.notes}</Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="caption" color="text.secondary">Date de création</Typography>
                <Typography variant="body2">
                  {selectedDevice.created_at ? new Date(selectedDevice.created_at).toLocaleString('fr-FR') : 'N/A'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDevice} color="primary" variant="outlined">
            Modifier
          </Button>
          <Button onClick={handleOpenAssignSim} color="secondary" variant="outlined">
            Assigner une SIM
          </Button>
          <Button onClick={handleCloseDeviceDetail}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={editDeviceDialog} onClose={() => setEditDeviceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le Dispositif</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Modèle"
              value={deviceFormData.model}
              onChange={(e) => setDeviceFormData({ ...deviceFormData, model: e.target.value })}
              fullWidth
            />
            <TextField
              label="Notes"
              value={deviceFormData.notes}
              onChange={(e) => setDeviceFormData({ ...deviceFormData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDeviceDialog(false)}>Annuler</Button>
          <Button onClick={handleSaveDevice} variant="contained" color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign SIM Dialog */}
      <Dialog open={assignSimDialog} onClose={() => setAssignSimDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assigner une Carte SIM</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Carte SIM Disponible</InputLabel>
              <Select
                value={selectedSim}
                label="Carte SIM Disponible"
                onChange={(e) => setSelectedSim(e.target.value)}
              >
                <MenuItem value="">
                  <em>Sélectionner une carte SIM</em>
                </MenuItem>
                {availableSims.map((sim) => (
                  <MenuItem key={sim.id} value={sim.id}>
                    {sim.sim_number} - {sim.iccid} ({sim.provider_name || 'Sans opérateur'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {availableSims.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Aucune carte SIM disponible. Toutes les cartes SIM sont déjà assignées.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignSimDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleAssignSim} 
            variant="contained" 
            color="primary"
            disabled={!selectedSim}
          >
            Assigner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit SIM Dialog */}
      <Dialog open={editSimDialog} onClose={() => setEditSimDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier la Carte SIM</DialogTitle>
        <DialogContent>
          {editingSimData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="ICCID"
                value={editingSimData.iccid}
                onChange={(e) => setEditingSimData({ ...editingSimData, iccid: e.target.value })}
                fullWidth
              />
              <TextField
                label="Numéro SIM"
                value={editingSimData.sim_number}
                onChange={(e) => setEditingSimData({ ...editingSimData, sim_number: e.target.value })}
                fullWidth
              />
              <TextField
                label="Notes"
                value={editingSimData.notes}
                onChange={(e) => setEditingSimData({ ...editingSimData, notes: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSimDialog(false)}>Annuler</Button>
          <Button onClick={handleSaveSim} variant="contained" color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default VehiclesPage;
